import * as React from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {BulletedList} from '../../../bulleted-list/bulleted-list';
import {CheckmarkButton} from '../../../button/checkmark-button/checkmark-button';
import {CloseButton} from '../../../button/close-button/close-button';
import {colors} from '../../../colors/colors';
import {LoadingIcon} from '../../../icon/svg/loading-icon';
import {BaseRow} from '../../../row/base-row/base-row';
import {TextField} from '../../../textfield/textfield';

import {PotentialPeepsDropdown} from '../../potential-peeps-dropdown/potential-peeps-dropdown';

import type {AccountInput} from '../types';

import {FIELD_NAME, FIELD_ADDRESS, FIELD_DESCRIPTION, FIELD_ROLE} from './consts';
import {shouldShowPotentialAccounts, shouldAllowCreate, handleKeyboardNavigation} from './helpers';
import type {AccountFormFieldTypes} from './types';

type Props = {
    temporaryAccount?: AccountInput;
    onSelectAccount: (arg1: DisplayOptionEntity) => void;
    onSubmit: (arg1: AccountInput) => void;
    onSearchPotentialAccounts?: (arg1: AccountInput) => Promise<any>;
    onClose: () => void;
    isPotentialAccountsPopover?: boolean;
    showRoleField?: boolean;
    showDescriptionField?: boolean;
    isSaving?: boolean;
};

export function AccountCardCreateable(props: Props) {
    const {showRoleField = false, showDescriptionField = true} = props;

    const [name, setName] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.name ? props.temporaryAccount.name : ''
    );
    const [description, setDescription] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.description
            ? props.temporaryAccount.description
            : ''
    );
    const [address, setAddress] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.address
            ? props.temporaryAccount.address
            : ''
    );
    const [role, setRole] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.jobTitle
            ? props.temporaryAccount.jobTitle
            : ''
    );
    const [potentialAccounts, setPotentialAccounts] = React.useState<DisplayOptionEntity[]>([]);
    const [formErrors, setFormErrors] = React.useState<string[]>([]);

    const nameRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const addressRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const descriptionRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const roleRef = React.useRef<HTMLInputElement | undefined | null>(null);
    const popoverRef = React.useRef<HTMLElement | undefined | null>(null);

    const shouldShowPotentialAccountsComponent = shouldShowPotentialAccounts(
        name,
        description,
        address,
        role
    );
    const shouldAllowCreateAccount = shouldAllowCreate(
        Boolean(props.temporaryAccount),
        name,
        address
    );

    React.useEffect(() => {
        if (shouldShowPotentialAccountsComponent) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            props.onSearchPotentialAccounts &&
                props
                    .onSearchPotentialAccounts({
                        name,
                        description,
                        address,
                    })
                    .then((matchedAccounts: DisplayOptionEntity[]) => {
                        setPotentialAccounts(matchedAccounts);
                    });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, description, address]);

    const handleSubmitAccount = () => {
        if (props.isSaving) return;
        if (!name.trim() && !address.trim()) {
            setFormErrors(['At least a company name or location is required']);

            return;
        }
        setFormErrors([]);
        props.onSubmit({
            name,
            description,
            address,
            jobTitle: role,
        });
    };

    const handleEnter = (e: React.KeyboardEvent) => {
        e.preventDefault();
        handleSubmitAccount();
    };

    const getFieldHasError = () => {
        return formErrors.length > 0;
    };

    const handleKeyDown = (e: React.KeyboardEvent, currentField: AccountFormFieldTypes) => {
        handleKeyboardNavigation(
            e,
            showRoleField,
            showDescriptionField,
            currentField,
            addressRef,
            descriptionRef,
            nameRef,
            roleRef
        );
    };

    return (
        <>
            <BaseRow getRowRef={popoverRef} type='accounts'>
                <div className='flex flex-dir-col gap-8'>
                    <div className='flex gap-8'>
                        <div className='flex flex-dir-col gap-8 full-width'>
                            <TextField
                                placeholder='Company name'
                                value={name}
                                onChange={(val) => {
                                    setName(val);
                                    setFormErrors([]);
                                }}
                                onEnter={handleEnter}
                                // @ts-expect-error
                                autoFocus={true}
                                size='small'
                                ignorePasswordAutoFill={true}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_NAME)}
                                textFieldRef={nameRef}
                                hasError={getFieldHasError()}
                            />
                            {showDescriptionField && (
                                <TextField
                                    placeholder='Description'
                                    value={description}
                                    onEnter={handleEnter}
                                    onChange={(val) => setDescription(val)}
                                    size='small'
                                    ignorePasswordAutoFill={true}
                                    onKeyDown={(e) => handleKeyDown(e, FIELD_DESCRIPTION)}
                                    textFieldRef={descriptionRef}
                                />
                            )}
                            <TextField
                                placeholder='Location'
                                size='small'
                                value={address}
                                onEnter={handleEnter}
                                onChange={(val) => {
                                    setAddress(val);
                                    setFormErrors([]);
                                }}
                                ignorePasswordAutoFill={true}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_ADDRESS)}
                                textFieldRef={addressRef}
                                hasError={getFieldHasError()}
                            />
                            {showRoleField && (
                                <TextField
                                    placeholder='Role'
                                    value={role || ''}
                                    onChange={(val) => {
                                        setRole(val);
                                        setFormErrors([]);
                                    }}
                                    size='small'
                                    ignorePasswordAutoFill={true}
                                    onKeyDown={(e) => handleKeyDown(e, FIELD_ROLE)}
                                    onEnter={handleEnter}
                                    textFieldRef={roleRef}
                                />
                            )}
                        </div>
                        <div className='flex flex-dir-col align-center gap-8'>
                            {props.isSaving ? (
                                <LoadingIcon size={22} fill={colors.green} wrapWithDiv={false} />
                            ) : (
                                <>
                                    {props.temporaryAccount && (
                                        <CheckmarkButton
                                            className='no-padding'
                                            // @ts-expect-error
                                            size={12}
                                            onClick={handleSubmitAccount}
                                            variant='dark'
                                        />
                                    )}
                                    <CloseButton
                                        className='no-padding'
                                        size={12}
                                        onClick={props.onClose}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    {formErrors && formErrors.length > 0 && (
                        <BulletedList
                            bullets={formErrors.map((error) => error)}
                            variant='errors'
                            size='small'
                            marginBottom={8}
                        />
                    )}
                </div>
            </BaseRow>
            {shouldShowPotentialAccountsComponent && !props.temporaryAccount && (
                <PotentialPeepsDropdown
                    potentialPeeps={potentialAccounts}
                    onSelectPeep={props.onSelectAccount}
                    popoverRef={popoverRef}
                    isPopover={props.isPotentialAccountsPopover}
                    createButtonProps={{
                        shouldShow: Boolean(shouldAllowCreateAccount),
                        onCreate: handleSubmitAccount,
                        entityName: name,
                        entityType: 'company',
                    }}
                />
            )}
        </>
    );
}
